import { browser } from '$app/environment';

const STORAGE_KEY = 'heyweek_scroll_position';
const SCROLL_RESTORE_DELAY = 50;

interface ScrollPosition {
	x: number;
	y: number;
	path: string;
	timestamp: number;
}

class ScrollPositionManager {
	private isRestoring = false;
	private pendingRestore: ScrollPosition | null = null;

	constructor() {
		if (browser) {
			this.initialize();
		}
	}

	private initialize() {
		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual';
		}

		this.attachListeners();
		this.restorePosition();
	}

	private attachListeners() {
		let scrollTimeout: number;
		
		const savePosition = () => {
			if (this.isRestoring) return;
			
			const position: ScrollPosition = {
				x: window.scrollX,
				y: window.scrollY,
				path: window.location.pathname,
				timestamp: Date.now()
			};
			
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(position));
		};

		window.addEventListener('scroll', () => {
			clearTimeout(scrollTimeout);
			scrollTimeout = window.setTimeout(savePosition, 100);
		}, { passive: true });

		window.addEventListener('beforeunload', savePosition);
		
		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				savePosition();
			}
		});
	}

	private restorePosition() {
		try {
			const stored = sessionStorage.getItem(STORAGE_KEY);
			if (!stored) return;

			const position: ScrollPosition = JSON.parse(stored);
			
			if (position.path !== window.location.pathname) {
				sessionStorage.removeItem(STORAGE_KEY);
				return;
			}

			const timeSinceStore = Date.now() - position.timestamp;
			if (timeSinceStore > 30000) {
				sessionStorage.removeItem(STORAGE_KEY);
				return;
			}

			this.pendingRestore = position;
			this.attemptRestore();
		} catch (e) {
			console.error('Failed to restore scroll position:', e);
			sessionStorage.removeItem(STORAGE_KEY);
		}
	}

	private attemptRestore() {
		if (!this.pendingRestore) return;

		const restore = () => {
			if (!this.pendingRestore) return;
			
			this.isRestoring = true;
			
			const { x, y } = this.pendingRestore;
			window.scrollTo(x, y);
			
			requestAnimationFrame(() => {
				if (Math.abs(window.scrollY - y) < 5 || window.scrollY + window.innerHeight >= document.body.scrollHeight) {
					this.pendingRestore = null;
					sessionStorage.removeItem(STORAGE_KEY);
					setTimeout(() => {
						this.isRestoring = false;
					}, 100);
				} else {
					setTimeout(() => this.attemptRestore(), SCROLL_RESTORE_DELAY);
				}
			});
		};

		if (document.readyState === 'complete') {
			requestAnimationFrame(restore);
		} else {
			window.addEventListener('load', () => {
				setTimeout(restore, SCROLL_RESTORE_DELAY);
			}, { once: true });
		}
	}

	public reset() {
		sessionStorage.removeItem(STORAGE_KEY);
		this.pendingRestore = null;
		this.isRestoring = false;
	}
}

export const scrollPositionManager = browser ? new ScrollPositionManager() : null;